import React from 'react'
import classnames from 'classnames'
import { CELL_COLOR } from '../cellColor'

// Period-over-period delta indicator on a KPI tile (WEB-6578): triangle glyph +
// signed change + color. `higherIsBetter` encodes direction-of-good explicitly —
// green means "good" for THIS metric, not "value went up" (e.g. a falling
// Unbilled $ or Avg Days to Close is green). Unit 'pct' renders a relative
// percent change; 'pp' renders a percentage-point change (for percentage-valued
// metrics like % Billed). A zero change renders neutral with no arrow.
export interface KpiDeltaProps {
  /** Change vs the prior period, already in display units (relative % or pp). */
  value: number
  unit?: 'pct' | 'pp'
  /** Does a higher value mean "good" (green) for this metric? */
  higherIsBetter: boolean
  /** Hover text, e.g. "vs prior month". */
  title?: string
}

export const KpiDelta: React.FC<KpiDeltaProps> = ({ value, unit = 'pct', higherIsBetter, title }) => {
  const rounded = Math.round(value * 10) / 10
  const suffix = unit === 'pp' ? ' pp' : '%'
  if (rounded === 0) {
    return (
      <span className="text-xs font-medium text-slate-400 dark:text-slate-500" title={title}>
        ±0{suffix}
      </span>
    )
  }
  const up = rounded > 0
  const good = up === higherIsBetter
  return (
    <span
      title={title}
      className={classnames(
        'inline-flex items-center gap-0.5 whitespace-nowrap text-xs font-medium',
        good ? CELL_COLOR.positive : CELL_COLOR.negative
      )}
    >
      <span aria-hidden>{up ? '▲' : '▼'}</span>
      {/* toFixed carries the leading '-' for negatives; add '+' for positives. */}
      {up ? '+' : ''}
      {rounded.toFixed(1)}
      {suffix}
    </span>
  )
}

export interface KpiItem {
  label: string
  value: React.ReactNode
  sublabel?: React.ReactNode
  /** Optional period-over-period delta indicator, shown beside the value. */
  delta?: React.ReactNode
  /** Native hover tooltip on the tile explaining the statistic (WEB-6578). */
  tooltip?: string
  /** Optional class for the headline value (e.g. semantic revenue / loss coloring). */
  valueClassName?: string
}

export interface KpiStripProps {
  items: KpiItem[]
  className?: string
}

// Horizontal strip of headline KPIs. Light/dark aware, wraps on narrow screens.
export const KpiStrip: React.FC<KpiStripProps> = ({ items, className }) => (
  <div className={classnames('grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4', className)}>
    {items.map((item) => (
      <div
        key={item.label}
        title={item.tooltip}
        className={classnames(
          'rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3',
          item.tooltip != null && 'cursor-help'
        )}
      >
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{item.label}</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span
            className={classnames(
              'text-xl font-semibold',
              item.valueClassName ?? CELL_COLOR.primary
            )}
          >
            {item.value}
          </span>
          {item.delta}
        </div>
        {item.sublabel != null && <div className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">{item.sublabel}</div>}
      </div>
    ))}
  </div>
)

export default KpiStrip
